import { DotLottieWorker } from '@lottiefiles/dotlottie-react'
import classNames from 'classnames'
import { useEffect, useId, useState } from 'react'

import { DiamondAnimation } from '@shared'

import styles from './Card.module.scss'
import { Lock } from './components/Lock'

type Props = {
  className?: string
  locked?: boolean
  withoutDots?: boolean
  disabled?: boolean
  movementAnimationDelay?: number
  children?: React.ReactNode
  title?: string
  index?: number
  onClick?: () => void
}

export const Card = ({
  movementAnimationDelay = 0,
  withoutDots = false,
  locked = false,
  disabled = false,
  title,
  children,
  onClick,
}: Props) => {
  const withDots = !withoutDots && !disabled
  const [dotLottie, setDotLottie] = useState<DotLottieWorker | null>(null)
  const [isLottyStarted, setIsLottyStarted] = useState(false)
  const [dotsData, setDotsData] = useState<ArrayBuffer | undefined>(undefined)

  const filter0Id = useId()
  const paint0LinearId = useId()
  const paint1LinearId = useId()
  const paint2DiamondId = useId()
  const filter17Id = useId()
  const filter18Id = useId()

  useEffect(() => {
    if (!dotLottie) return

    let timeout: number | null = null
    dotLottie.addEventListener('load', () => {
      timeout = window.setTimeout(() => {
        dotLottie.play()
        setIsLottyStarted(true)
      }, Math.random() * 1000)
    })

    return () => {
      if (!timeout) return
      clearTimeout(timeout)
    }
  }, [dotLottie])

  useEffect(() => {
    if (!withDots) return

    fetch('/lottie/dots.lottie')
      .then((res) => res.arrayBuffer())
      .then((data) => {
        setDotsData(data)
      })
  }, [withDots])

  return (
    <div
      className={classNames(styles.card, {
        [styles['card__inactive']]: disabled
      })}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <svg viewBox="0 0 199 183" fill="none" className={styles.cardInner}>
        <g
          filter={withDots ? `url(#${filter0Id})` : 'none'}
          opacity={disabled ? '0.5' : '1'}
        >
          <rect
            x="50.4707"
            y="43"
            width="100"
            height="120"
            rx="6"
            fill="#292929"
          />
        </g>
        <g style={{ mixBlendMode: 'screen' }}>
          <rect
            x="50.9707"
            y="43.5"
            width="99"
            height="119"
            rx="5.5"
            fill="#292929"
            fillOpacity="0.01"
          />
          <rect
            x="50.9707"
            y="43.5"
            width="99"
            height="119"
            rx="5.5"
            stroke={disabled ? 'none' : `url(#${paint0LinearId})`}
          />
        </g>
        <g style={{ mixBlendMode: 'screen', opacity: withDots ? '0.5' : '0' }}>
          <rect
            x="47.4414"
            y="40.0001"
            width="106.059"
            height="126"
            rx="8.5"
            fill="#292929"
            fillOpacity="0.01"
          />
          <rect
            x="47.4414"
            y="40.0001"
            width="106.059"
            height="126"
            rx="8.5"
            stroke={`url(#${paint1LinearId})`}
          />
        </g>
        {withDots && !disabled && <DiamondAnimation optimized />}
        <g style={{ opacity: withDots ? '1' : '0' }}>
          <g
            style={{ mixBlendMode: 'plus-lighter' }}
            filter={`url(#${filter17Id})`}
          >
            <path
              d="M143.571 43.9302L107 43.5L145.806 40.1973C150.103 39.8317 153.705 43.4002 153.38 47.7L149.5 99V49.9298C149.5 46.6437 146.857 43.9689 143.571 43.9302Z"
              fill="white"
            />
          </g>
          <g
            style={{ mixBlendMode: 'plus-lighter' }}
            filter={`url(#${filter18Id})`}
          >
            <path
              d="M58.2395 161.83L69.2189 162.242L54.7125 162.748C52.3881 162.829 50.4708 160.945 50.5116 158.619L50.8735 138L51.5066 155.094C51.6422 158.756 54.5782 161.693 58.2395 161.83Z"
              fill="white"
            />
          </g>
        </g>
        <defs>
          <filter
            id={filter0Id}
            x="50.4707"
            y="43"
            width="100"
            height="120"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset />
            <feGaussianBlur stdDeviation="10" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.65 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect1_innerShadow_133_1126"
            />
          </filter>
          <filter
            id={filter17Id}
            x="102"
            y="35.1715"
            width="56.4004"
            height="68.8285"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="2.5"
              result="effect1_foregroundBlur_133_1126"
            />
          </filter>
          <filter
            id={filter18Id}
            x="49.085"
            y="136.574"
            width="21.5594"
            height="27.6021"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="0.712851"
              result="effect1_foregroundBlur_133_1126"
            />
          </filter>
          <linearGradient
            id={paint0LinearId}
            x1="147.279"
            y1="36.2331"
            x2="221.256"
            y2="106.167"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#7F7F7F" />
            <stop offset="1" />
          </linearGradient>
          <linearGradient
            id={paint1LinearId}
            x1="150.583"
            y1="32.3384"
            x2="228.921"
            y2="107.252"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#7F7F7F" />
            <stop offset="1" />
          </linearGradient>
          <linearGradient
            id={paint2DiamondId}
            x1="0"
            y1="0"
            x2="500"
            y2="500"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#7F7F7F" />
            <stop offset="1" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
      {withDots && (
        <>
          <div
            className={styles.dots}
            style={{ opacity: isLottyStarted ? '1' : '0' }}
          >
            {/* {dotsData && (
              <DotLottieWorkerReact
                data={dotsData}
                dotLottieRefCallback={setDotLottie}
                loop
                autoplay
              />
            )} */}
          </div>
        </>
      )}
      <div
        className={classNames(styles.image, withDots && styles.imageActive)}
        style={{ animationDelay: `${movementAnimationDelay}s` }}
      >
        {children}
      </div>
      <div className={styles.title}>{title}</div>
      {locked && <Lock className={styles.lock} disabled={disabled} />}
    </div>
  )
}
